import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FlaskConical, Shield, Snowflake, FileText, ChevronRight, ScrollText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { PRODUCTS } from "@/data/siteData";
import { COA_DATA, getCoaKey } from "@/data/coaData";
import COADocument from "@/components/COADocument";

function ProductImage({ product }) {
  return (
    <div
      className="neon-border rounded-lg overflow-hidden relative"
      style={{ background: 'var(--bg-surface)' }}
    >
      <img
        src={product.image_url}
        alt={product.name}
        data-testid="product-image"
        className="w-full h-[400px] object-cover opacity-70"
      />
      <div className="absolute bottom-0 left-0 right-0 p-6" style={{ background: 'linear-gradient(transparent, var(--bg-surface))' }}>
        <Badge
          className="text-[10px] tracking-[0.15em] uppercase"
          style={{
            background: 'rgba(0,240,255,0.1)',
            border: '1px solid rgba(0,240,255,0.2)',
            color: 'var(--neon-cyan)',
          }}
        >
          {product.category}
        </Badge>
      </div>
      <div
        className="absolute top-4 right-4 text-[9px] tracking-[0.15em] uppercase px-2 py-1 rounded"
        style={{
          background: 'rgba(229,0,255,0.1)',
          border: '1px solid rgba(229,0,255,0.3)',
          color: 'var(--neon-magenta)',
          fontFamily: "'Roboto Mono', monospace",
        }}
      >
        Research Use Only
      </div>
    </div>
  );
}

function SpecificationsPanel({ product, selectedVariant }) {
  const specs = useMemo(() => [
    { label: "Purity", value: product.purity },
    { label: "Form", value: product.form },
    { label: "Storage", value: product.storage },
    product.cas_number ? { label: "CAS Number", value: product.cas_number } : null,
    product.molecular_formula ? { label: "Molecular Formula", value: product.molecular_formula } : null,
  ].filter(Boolean), [product.purity, product.form, product.storage, product.cas_number, product.molecular_formula]);

  return (
    <div
      className="neon-border rounded-lg p-6"
      style={{ background: 'var(--bg-surface)' }}
    >
      <h3 className="text-lg font-bold mb-4" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
        Specifications
      </h3>
      <div className="space-y-4">
        {specs.map((spec) => (
          <div key={spec.label}>
            <p className="text-[10px] tracking-[0.15em] uppercase mb-0.5" style={{ color: 'var(--neon-cyan)', fontFamily: "'Roboto Mono', monospace" }}>
              {spec.label}
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{spec.value}</p>
          </div>
        ))}
      </div>

      <Separator className="my-6" style={{ backgroundColor: 'rgba(0,240,255,0.08)' }} />

      <h4 className="text-sm font-bold mb-3" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
        Available Sizes
      </h4>
      <div className="space-y-2">
        {product.variants.map((v) => (
          <div
            key={v.mg}
            className="flex items-center justify-between p-2.5 rounded text-sm"
            style={{
              background: selectedVariant?.mg === v.mg ? 'rgba(0,240,255,0.05)' : 'transparent',
              border: `1px solid ${selectedVariant?.mg === v.mg ? 'rgba(0,240,255,0.3)' : 'rgba(255,255,255,0.04)'}`,
              fontFamily: "'Roboto Mono', monospace",
            }}
          >
            <span style={{ color: 'var(--text-secondary)' }}>{v.mg}</span>
            <span className="font-semibold" style={{ color: 'var(--neon-cyan)' }}>${v.price}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DisclaimerCard() {
  return (
    <div
      className="neon-border-magenta rounded-lg p-6 mt-6"
      style={{ background: 'var(--bg-surface)' }}
    >
      <Shield className="w-5 h-5 mb-3" style={{ color: 'var(--neon-magenta)' }} />
      <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
        This product is intended for laboratory and research use only. Not for human consumption, therapeutic use, or diagnostic purposes. Must be handled by qualified research personnel.
      </p>
    </div>
  );
}

const QUICK_SPECS = [
  { icon: FlaskConical, label: "Purity", valueKey: "purity" },
  { icon: FileText, label: "Form", valueKey: "form" },
  { icon: Snowflake, label: "Storage", valueKey: null, staticValue: "-20\u00B0C" },
  { icon: Shield, label: "Grade", valueKey: null, staticValue: "Research" },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [showCoa, setShowCoa] = useState(false);

  const variant = selectedVariant || product?.variants[0];
  const coaKey = product ? getCoaKey(product.slug, variant?.mg || product.variants[0].mg) : null;
  const currentCoa = coaKey ? COA_DATA[coaKey] : null;

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-4">
        <p className="text-lg" style={{ fontFamily: "'Rajdhani', sans-serif" }}>Product not found</p>
        <Link to="/products" className="btn-ghost-neon px-6 py-2 rounded text-sm">Back to Catalog</Link>
      </div>
    );
  }

  return (
    <div className="grid-bg min-h-screen">
      {/* Breadcrumb */}
      <div style={{ borderBottom: '1px solid rgba(0,240,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)', fontFamily: "'Roboto Mono', monospace" }}>
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/products" className="hover:text-white transition-colors">Products</Link>
          <ChevronRight className="w-3 h-3" />
          <span style={{ color: 'var(--neon-cyan)' }}>{product.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <Link
          to="/products"
          data-testid="back-to-catalog-btn"
          className="inline-flex items-center gap-2 text-sm mb-10 transition-colors duration-300 hover:text-white"
          style={{ color: 'var(--text-secondary)', fontFamily: "'Rajdhani', sans-serif", fontWeight: 600 }}
        >
          <ArrowLeft className="w-4 h-4" /> BACK TO CATALOG
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <ProductImage product={product} />

          <div className="fade-in-up">
            <h1
              data-testid="product-name"
              className="text-4xl sm:text-5xl font-bold tracking-tighter mb-2"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              {product.name}
            </h1>

            {product.cas_number && (
              <p className="text-xs mb-6" style={{ color: 'var(--text-secondary)', fontFamily: "'Roboto Mono', monospace" }}>
                CAS: {product.cas_number}
              </p>
            )}

            <p className="text-sm leading-relaxed mb-8" style={{ color: 'var(--text-secondary)' }}>
              {product.short_description}
            </p>

            {/* Variant selector */}
            {product.variants.length > 1 && (
              <div className="mb-8">
                <label
                  className="text-xs tracking-[0.15em] uppercase block mb-3"
                  style={{ color: 'var(--neon-cyan)', fontFamily: "'Roboto Mono', monospace" }}
                >
                  Select Quantity
                </label>
                <Select
                  value={variant.mg}
                  onValueChange={(val) =>
                    setSelectedVariant(product.variants.find((v) => v.mg === val))
                  }
                >
                  <SelectTrigger
                    data-testid="variant-select-trigger"
                    className="w-full sm:w-64 h-12 text-sm"
                    style={{
                      background: 'var(--bg-surface)',
                      border: '1px solid rgba(0,240,255,0.3)',
                      color: 'var(--text-primary)',
                      fontFamily: "'Roboto Mono', monospace",
                    }}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ background: 'var(--bg-surface)', border: '1px solid rgba(0,240,255,0.2)' }}>
                    {product.variants.map((v) => (
                      <SelectItem key={v.mg} value={v.mg} data-testid={`variant-option-${v.mg}`} className="text-sm cursor-pointer" style={{ fontFamily: "'Roboto Mono', monospace" }}>
                        {v.mg} &mdash; ${v.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Price */}
            <div className="mb-8">
              <span className="text-[11px] tracking-[0.15em] uppercase block mb-1" style={{ color: 'var(--text-secondary)', fontFamily: "'Roboto Mono', monospace" }}>
                {variant.mg}
              </span>
              <span data-testid="product-price" className="text-4xl font-bold neon-text-cyan" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                ${variant.price}
              </span>
            </div>

            <Link
              to="/contact"
              data-testid="inquire-about-product-btn"
              className="btn-neon px-10 py-3 rounded text-sm inline-block mb-4"
            >
              INQUIRE ABOUT THIS PRODUCT
            </Link>

            {/* View COA button */}
            {currentCoa && (
              <button
                data-testid="view-coa-btn"
                onClick={() => setShowCoa(true)}
                className="btn-ghost-neon px-8 py-3 rounded text-sm inline-flex items-center gap-2 mb-8 ml-0 sm:ml-4"
              >
                <ScrollText className="w-4 h-4" />
                VIEW COA ({variant.mg})
              </button>
            )}

            {/* Quick specs */}
            <div className="grid grid-cols-2 gap-4">
              {QUICK_SPECS.map(({ icon: Icon, label, valueKey, staticValue }) => (
                <div key={label} className="p-3 rounded" style={{ background: 'var(--bg-surface)', border: '1px solid rgba(0,240,255,0.08)' }}>
                  <Icon className="w-4 h-4 mb-1" style={{ color: 'var(--neon-cyan)' }} />
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-secondary)', fontFamily: "'Roboto Mono', monospace" }}>{label}</p>
                  <p className="text-sm font-semibold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{staticValue || product[valueKey]}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Separator className="my-16" style={{ backgroundColor: 'rgba(0,240,255,0.08)' }} />

        {/* Detailed info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 pb-16">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold tracking-tight mb-6" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              Compound Overview
            </h2>
            <p className="text-sm leading-relaxed mb-8" style={{ color: 'var(--text-secondary)' }}>
              {product.description}
            </p>

            <h3 className="text-xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              Mechanism of Action
            </h3>
            <p className="text-sm leading-relaxed mb-8" style={{ color: 'var(--text-secondary)' }}>
              {product.mechanism}
            </p>

            <h3 className="text-xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              Research Applications
            </h3>
            <ul className="space-y-2">
              {product.research_applications.map((app) => (
                <li key={app} className="flex items-start gap-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: 'var(--neon-cyan)' }} />
                  {app}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <SpecificationsPanel product={product} selectedVariant={variant} />
            <DisclaimerCard />
          </div>
        </div>
      </div>

      {/* COA Modal */}
      {showCoa && currentCoa && (
        <COADocument coa={currentCoa} onClose={() => setShowCoa(false)} />
      )}
    </div>
  );
}
